import { isUser } from '@/models/role';
import User from '@/models/user';
import { tenantsService } from '@/services/tenant.service';
import { tenantSettingsService } from '@/services/tenant-settings.service';

const initialState = () => ({
  tenant: {
    websiteUrl: null,
  },
  normalizedNames: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getTenant({ commit }, tenantId) {
    commit('GET_TENANT');
    return tenantsService.getTenantById(tenantId).then(
      (tenant) => {
        commit('SUCCESS', tenant);
        return tenant;
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },
  updateTenant({ commit }, { tenantId, tenant }) {
    commit('UPDATE_TENANT');
    return tenantsService.updateTenant({ tenantId, tenant }).then(
      (tenant) => {
        commit('UPDATE_TENANT_SUCCESS', tenant);
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },
  getNormalizedNames({ commit }) {
    commit('GET_NORMALIZED_NAMES');
    return tenantSettingsService.getTenantNormalizedNames().then(
      (result) => {
        commit('GET_NORMALIZED_NAMES_SUCCESS', result);
      },
      (error) => {
        commit('ERROR', error.response);
      },
    );
  },
  updateTenantDescription({ commit }, description) {
    commit('UPDATE_TENANT_DESCRIPTION');
    return tenantSettingsService.updateTenantDescription(description).then(
      () => {
        commit('UPDATE_TENANT_DESCRIPTION_SUCCESS');
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  generateTenantDescriptionPreview({ commit }, url) {
    commit('GENERATE_TENANT_DESCRIPTION_PREVIEW');
    return tenantSettingsService.generateTenantDescriptionPreview(url).then(
      (res) => {
        commit('GENERATE_TENANT_DESCRIPTION_PREVIEW_SUCCESS');
        return res;
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  approveTenantDescriptionPreview({ commit }, description) {
    commit('APPROVE_TENANT_DESCRIPTION_PREVIEW');
    return tenantSettingsService.approveTenantDescriptionPreview(description).then(
      () => {
        commit('APPROVE_TENANT_DESCRIPTION_PREVIEW_SUCCESS');
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  getTenantWebsite({ commit }) {
    commit('GET_TENANT_WEBSITE');
    return tenantSettingsService.getTenantWebsite().then(
      ({ url }) => {
        commit('GET_TENANT_WEBSITE_SUCCESS', url);
        return url;
      },
      (error) => {
        if (error.response.data.code === 'NOT_FOUND') {
          commit('GET_TENANT_WEBSITE_SUCCESS', null);
          return null;
        }
        commit('ERROR', error);
        throw error;
      },
    );
  },
  updateTenantWebsite({ commit }, url) {
    commit('UPDATE_TENANT_WEBSITE');
    return tenantSettingsService.upsertTenantWebsite(url).then(
      ({ url }) => {
        commit('UPDATE_TENANT_WEBSITE_SUCCESS', url);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
};

const mutations = {
  GET_TENANT(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_NORMALIZED_NAMES(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SUCCESS(state, tenant) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tenant = {
      ...state.tenant,
      ...tenant,
    };
    if (state.tenant.users && state.tenant.users.length) {
      state.tenant.users = state.tenant.users.map((user) => User.hydrate(user));
    }
  },
  UPDATE_TENANT(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  UPDATE_TENANT_SUCCESS(state, tenant) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tenant = {
      ...state.tenant,
      ...tenant,
    };
  },
  GET_NORMALIZED_NAMES_SUCCESS(state, response) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.normalizedNames = response;
  },
  UPDATE_TENANT_DESCRIPTION(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  UPDATE_TENANT_DESCRIPTION_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  UPDATE_NORMALIZED_NAME(state, updatedNormalizedName) {
    const index = state.normalizedNames.findIndex((normalizedName) => normalizedName.id === updatedNormalizedName.id);
    if (index !== -1) {
      state.normalizedNames.splice(index, 1, updatedNormalizedName);
    }
  },
  DELETE_NORMALIZED_NAME(state, deletedNormalizedNameId) {
    const index = state.normalizedNames.findIndex((normalizedName) => normalizedName.id === deletedNormalizedNameId);
    if (index !== -1) {
      state.normalizedNames.splice(index, 1);
    }
  },
  APPROVE_TENANT_DESCRIPTION_PREVIEW(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  APPROVE_TENANT_DESCRIPTION_PREVIEW_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  GENERATE_TENANT_DESCRIPTION_PREVIEW(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  GENERATE_TENANT_DESCRIPTION_PREVIEW_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  GET_TENANT_WEBSITE(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_TENANT_WEBSITE_SUCCESS(state, url) {
    state.status = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.tenant.websiteUrl = url;
  },
  UPDATE_TENANT_WEBSITE(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATE_TENANT_WEBSITE_SUCCESS(state, url) {
    state.status = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.tenant.websiteUrl = url;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
};

const getters = {
  tenant: (state) => state.tenant,
  subscriptionType: (state) => state.tenant.subscriptionType || 'standard',
  users: (state) => state.tenant.users,
  normalizedNames: (state) => state.normalizedNames,

  // Different grouping of users by ROLE
  // Eventually, users could see admin_tenant users as well
  roleUserOnly: (state) => (state.tenant.users ? state.tenant.users.filter((user) => isUser(user.role)) : []),

  trialOn: (state) => state.tenant.trialOn,
  trialStartDate: (state) => state.tenant.trialStartDate,
  trialEndDate: (state) => state.tenant.trialEndDate,
  daysBeforeTrialEnds: (state) => state.tenant.daysBeforeTrialEnds,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const tenant = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
